import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilChevronBottom, cilChevronTop } from '@coreui/icons'
import './table.css'
import { wtRoundNumberToSpecifedDigits } from 'src/helpers/commons/utils'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'

const DETAIL_CONFIGS = {
  on_leave: {
    title: 'On Leave Dates',
    fields: [
      'on_leave_dates',
      'leave_dates',
      'leaves',
      'leave_details',
      'attendance_leaves',
      'attendance_details',
      'attendances',
      'employee_attendances',
    ],
    keywords: ['leave'],
  },
  on_move: {
    title: 'On Move Dates',
    fields: [
      'on_move_dates',
      'move_dates',
      'moves',
      'move_details',
      'attendance_moves',
      'attendance_details',
      'attendances',
      'employee_attendances',
    ],
    keywords: ['move'],
  },
  total_absent_unpresented: {
    title: 'Absent/Unpresented Dates',
    fields: [
      'absent_unpresented_dates',
      'absent_dates',
      'unpresented_dates',
      'absents',
      'absent_details',
      'attendance_absents',
      'attendance_details',
      'attendances',
      'employee_attendances',
    ],
    keywords: ['absent', 'unpresented', 'unpresent'],
  },
  total_shift_hours: {
    title: 'Total Shift Hours Breakup',
    fields: ['shift_hour_details', 'shift_hours_details', 'total_shift_hours_details'],
  },
  total_hours: {
    title: 'Total Hours Breakup',
    fields: ['total_hour_details', 'total_hours_details', 'attendance_hour_details'],
  },
  total_working_hours: {
    title: 'Working Hours Breakup',
    fields: ['working_hour_details', 'working_hours_details', 'total_working_hours_details'],
  },
  total_difference: {
    title: 'This Month Difference Breakup',
    fields: [
      'difference_details',
      'total_difference_details',
      'month_difference_details',
      'attendance_details',
      'attendances',
      'employee_attendances',
    ],
    keywords: ['difference', 'diff'],
  },
  total_difference_last_month: {
    title: 'Last Month Difference Breakup',
    fields: ['last_month_difference_details', 'total_difference_last_month_details'],
  },
}

const dateFields = ['attendance_date', 'date', 'leave_date', 'move_date', 'absent_date', 'day']
const valueFields = [
  'value',
  'hours',
  'working_hours',
  'shift_hours',
  'difference',
  'minutes',
  'status',
  'type',
  'remarks',
  'reason',
]

const formatDetailValue = (value) => {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'object') {
    return value.name || value.label || value.status || value.remarks || JSON.stringify(value)
  }

  return value
}

const getDetailValue = (detail, fields) => fields.find((field) => detail?.[field] !== undefined)

const normalizeDetailRows = (details, keywords = [], formatDate, formatDay) => {
  if (!details) {
    return []
  }

  if (Array.isArray(details)) {
    return details
      .filter((detail) => {
        if (keywords.length === 0 || typeof detail !== 'object' || detail === null) {
          return true
        }

        const searchableDetail = JSON.stringify(detail).toLowerCase()
        return keywords.some((keyword) => searchableDetail.includes(keyword))
      })
      .map((detail) => {
      if (typeof detail !== 'object' || detail === null) {
          return { date: formatDate(detail), day: formatDay(detail), value: '' }
        }

        const dateField = getDetailValue(detail, dateFields)
        const valueField = getDetailValue(detail, valueFields)
        const value = valueField ? detail[valueField] : ''

        return {
          date: dateField ? formatDate(detail[dateField]) : '',
          day: dateField ? formatDay(detail[dateField]) : '',
          value: formatDetailValue(value),
        }
      })
  }

  if (typeof details === 'object') {
    return Object.keys(details).map((date) => ({
      date: formatDate(date),
      day: formatDay(date),
      value: formatDetailValue(details[date]),
    }))
  }

  return []
}

export default function DataReportTable({ reportData, setReportData, isOfficer }) {
  const { formatDate: formatMasterDate } = useMasterDateTimeFormats()
  const [sortedKey, setSortedKey] = useState('')
  const [detailModal, setDetailModal] = useState({
    visible: false,
    title: '',
    employeeName: '',
    total: '',
    rows: [],
  })
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  })

  const formatDetailDate = (value) => {
    if (!value) {
      return ''
    }

    const date = moment(value)
    return date.isValid() ? formatMasterDate(date) : value
  }

  const formatDetailDay = (value) => {
    if (!value) {
      return ''
    }

    const date = moment(value)
    return date.isValid() ? date.format('dddd') : ''
  }

  const sortTable = (key) => {
    let direction = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    const sorted = [...reportData].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1
      }
      return 0
    })
    setReportData(sorted)
    setSortedKey(key)
    setSortConfig({ key, direction })
  }

  const closeDetailModal = () => {
    setDetailModal({
      visible: false,
      title: '',
      employeeName: '',
      total: '',
      rows: [],
    })
  }

  const openDetailModal = (employee, key, total) => {
    const config = DETAIL_CONFIGS[key]
    const detailField = config.fields.find((field) => employee?.[field])

    setDetailModal({
      visible: true,
      title: config.title,
      employeeName: employee.full_name,
      total,
      rows: normalizeDetailRows(
        detailField ? employee[detailField] : [],
        config.keywords || [],
        formatDetailDate,
        formatDetailDay,
      ),
    })
  }

  const renderDetailValue = (employee, key, value, style = {}) => (
    <button
      type="button"
      className="attendance-detail-link"
      style={style}
      onClick={() => openDetailModal(employee, key, value)}
      title="Click to view breakup"
    >
      {value}
    </button>
  )

  return (
    <>
      <thead class="thead-dark" style={{ backgroundColor: '#e8e9eb' }}>
        <tr>
          <th onClick={() => sortTable('id')}>Sr</th>
          <th onClick={() => sortTable('id')}>
            ID{' '}
            {sortedKey === 'id' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('full_name')}>
            Full Name
            {sortedKey === 'full_name' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>

          <th onClick={() => sortTable('designations')}>
            Designation
            {sortedKey === 'designations' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('total_hours')}>
            Total Shift Hours
            {sortedKey === 'total_hours' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('on_leave')}>
            On Leave (Days)
            {sortedKey === 'on_leave' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('on_move')}>
            On Move (Days)
            {sortedKey === 'on_move' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('total_absent_unpresented')}>
            Absent/Unpresented
            {sortedKey === 'total_absent_unpresented' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('total_hours')}>
            Total Hours
            {sortedKey === 'total_hours' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('total_working_hours')}>
            Working Hours
            {sortedKey === 'total_working_hours' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('difference')}>
            Diff this Month (hrs)
            {sortedKey === 'difference' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>
          <th onClick={() => sortTable('difference')}>
            Difference Last Month  (hrs)
            {sortedKey === 'difference' && (
              <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
            )}
          </th>

        </tr>
      </thead>
      <tbody>
        {reportData.map((item, index) => (
          <tr key={item.id}>
            <td>{index + 1}</td>
            <td style={{ color: 'black' }}>
              <Link
                to={`/employee-profile?id=${item.id}&tab=emp_attendance`}
                target="_blank"
              >
                {item.id}
              </Link>
            </td>
            <td style={{ color: 'blue' }}>
              <Link
                to={`/employee-profile?id=${item.id}&tab=emp_attendance`}
                target="_blank"
              >
                {item.full_name}
              </Link>
            </td>
            <td style={{ color: 'blue', textAlign: 'center' }} >{item.designation.name}</td>
            <td style={{ textAlign: 'center' }}>
              {renderDetailValue(
                item,
                'total_shift_hours',
                wtRoundNumberToSpecifedDigits(item.total_shift_hours),
                { color: 'blue' },
              )}
            </td>
            <td style={{ textAlign: 'center' }}>
              {renderDetailValue(item, 'on_leave', item.on_leave ?? 0, { color: 'blue' })}
            </td>
            <td style={{ textAlign: 'center' }}>
              {renderDetailValue(item, 'on_move', item.on_move ?? 0, { color: 'blue' })}
            </td>
            <td style={{ color: item.total_absent_unpresented > 0 ? 'red' : 'blue', textAlign: 'center' }}>
              {renderDetailValue(
                item,
                'total_absent_unpresented',
                item.total_absent_unpresented ?? 0,
                { color: item.total_absent_unpresented > 0 ? 'red' : 'blue' },
              )}
            </td>
            <td style={{ textAlign: 'center' }}>
              {renderDetailValue(
                item,
                'total_hours',
                wtRoundNumberToSpecifedDigits(item.total_hours),
                { color: 'blue' },
              )}
            </td>
            <td style={{ textAlign: 'center' }}>
              {renderDetailValue(
                item,
                'total_working_hours',
                wtRoundNumberToSpecifedDigits(item.total_working_hours),
                { color: 'blue' },
              )}
            </td>
            <td style={{ color: item.total_difference < 0 ? 'red' : 'blue', textAlign: 'center' }}>
              {renderDetailValue(
                item,
                'total_difference',
                wtRoundNumberToSpecifedDigits(item.total_difference),
                { color: item.total_difference < 0 ? 'red' : 'blue' },
              )}
            </td>
            <td style={{ color: item.total_difference_last_month < 0 ? 'red' : 'blue', textAlign: 'center' }}>
              {renderDetailValue(
                item,
                'total_difference_last_month',
                wtRoundNumberToSpecifedDigits(item.total_difference_last_month),
                { color: item.total_difference_last_month < 0 ? 'red' : 'blue' },
              )}
            </td>

            {/* <td style={{ color: item.total_deduction > 0 ? 'red' : 'black' }}>{item.total_deduction}</td> */}
          </tr>
        ))}
      </tbody>
      {typeof document !== 'undefined' &&
        createPortal(
          <CModal visible={detailModal.visible} onClose={closeDetailModal} size="lg">
            <CModalHeader style={{ backgroundColor: '#3c4b64' }} closeButton={false}>
              <CModalTitle className="text-white">{detailModal.title}</CModalTitle>
            </CModalHeader>
            <CModalBody>
              <div style={{ marginBottom: '12px' }}>
                <strong>Employee:</strong> {detailModal.employeeName}
                <br />
                <strong>Total:</strong> {detailModal.total}
              </div>
              {detailModal.rows.length > 0 ? (
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th style={{ width: '70px' }}>Sr</th>
                      <th>Date</th>
                      <th>Day</th>
                      <th>Value / Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailModal.rows.map((row, index) => (
                      <tr key={`${row.date}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{row.date || '-'}</td>
                        <td>{row.day || '-'}</td>
                        <td>{row.value !== '' ? row.value : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="alert alert-info mb-0">
                  Breakup details are not available in the report response yet. The total is
                  available, but the dates/remarks need to be sent by the API for this popup.
                </div>
              )}
            </CModalBody>
            <CModalFooter>
              <CButton color="secondary" onClick={closeDetailModal}>
                Close
              </CButton>
            </CModalFooter>
          </CModal>,
          document.body,
        )}
    </>
  )
}
